import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { format } from "date-fns";

function yesNo(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return value ?? "";
}

function formatExportDate(value) {
  if (!value) return "";
  return String(value).replace("T", " ").slice(0, 19);
}

export function teacherDetailFilename(regNumber, ext) {
  const reg = String(regNumber || "teacher").replace(/[^\w.-]+/g, "_");
  const stamp = format(new Date(), "yyyy-MM-dd");
  return `teacher-${reg}-${stamp}.${ext}`;
}

function buildProfileRows(profile, labels) {
  if (!profile) return [];
  return [
    [labels.fullName, profile.fullName || [profile.preName, profile.name].filter(Boolean).join(" ")],
    [labels.regNumber, profile.regNumber],
    [labels.gender, profile.gender],
    [labels.dob, formatExportDate(profile.dob)],
    [labels.regDate, formatExportDate(profile.regDate)],
    [labels.degree, profile.degree],
    [labels.status, profile.status],
    [labels.phone, profile.phone],
    [labels.whatsapp, profile.whatsapp],
    [labels.email, profile.email],
    [labels.recruiter, profile.recruiter],
    [labels.licensed, yesNo(profile.isLicensed)],
    [labels.allocated, yesNo(profile.isAllocated)],
    [labels.hasAttendance, yesNo(profile.hasAttendance)],
    [labels.score, profile.score],
    [labels.lastScoreDate, formatExportDate(profile.lastScoreDate)],
  ];
}

function buildSummaryRows(summary, labels) {
  if (!summary) return [];
  return [
    [labels.totalAttendance, summary.totalAttendance],
    [labels.thisMonth, summary.thisMonth],
    [labels.thisWeek, summary.thisWeek],
    [labels.lastAttendanceDate, formatExportDate(summary.lastAttendanceDate)],
    [labels.daysSinceLastAttendance, summary.daysSinceLastAttendance],
    [labels.firstAttendanceDate, formatExportDate(summary.firstAttendanceDate)],
    [labels.averageAttendancesPerMonth, summary.averageAttendancesPerMonth],
    [labels.uniqueEstablishments, summary.uniqueEstablishments],
    [labels.attendanceWithinRadius, summary.attendanceWithinRadius],
    [labels.attendanceOutsideRadius, summary.attendanceOutsideRadius],
    [
      labels.attendanceWithinRadiusPercent,
      summary.attendanceWithinRadiusPercent != null
        ? `${Number(summary.attendanceWithinRadiusPercent).toFixed(1)}%`
        : "",
    ],
  ];
}

function buildAssignmentRows(assignment, labels) {
  if (!assignment?.isAssigned) {
    return [[labels.assignmentStatus, labels.notAssigned]];
  }
  return [
    [labels.establishment, assignment.estabName],
    [labels.estabType, assignment.estabType],
    [labels.assignedDate, formatExportDate(assignment.assignedDate)],
    [labels.location, assignment.locationLabel],
    [labels.latitude, assignment.latitude],
    [labels.longitude, assignment.longitude],
  ];
}

function buildRankingRows(ranking, profile, labels) {
  if (!ranking && profile?.score == null) return [];
  return [
    [labels.overallRank, ranking?.overallRank],
    [labels.provinceRank, ranking?.provinceRank],
    [labels.provinceName, ranking?.provinceName],
    [labels.departmentRank, ranking?.departmentRank],
    [labels.departmentName, ranking?.departmentName],
    [labels.score, ranking?.score ?? profile?.score],
  ];
}

function buildAttendanceRows(recentAttendance, labels) {
  return (recentAttendance || []).map((row) => ({
    [labels.date]: [row.date, row.time].filter(Boolean).join(" "),
    [labels.establishment]: row.estabName ?? "",
    [labels.estabType]: row.estabType ?? "",
    [labels.dayOfWeek]: row.dayOfWeek ?? "",
    [labels.distanceMeters]: row.distanceMeters != null ? Number(row.distanceMeters).toFixed(0) : "",
    [labels.radiusStatus]: row.isWithinRadius ? labels.inRadius : labels.outRadius,
    [labels.imei]: row.imei ?? "",
  }));
}

function buildChartRows(charts, labels) {
  const monthly = (charts?.attendanceByMonth ?? []).map((row) => ({
    [labels.period]: row.label ?? `${row.year}-${String(row.month).padStart(2, "0")}`,
    [labels.count]: row.count ?? 0,
  }));
  const byDay = (charts?.attendanceByDayOfWeek ?? []).map((row) => ({
    [labels.dayOfWeek]: row.day ?? "",
    [labels.count]: row.count ?? 0,
  }));
  const byEstab = (charts?.attendanceByEstablishment ?? []).map((row) => ({
    [labels.establishment]: row.estabName ?? "",
    [labels.count]: row.count ?? 0,
  }));
  return { monthly, byDay, byEstab };
}

function kvSheet(section, rows) {
  return rows
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([field, value]) => ({
      Section: section,
      Field: field,
      Value: String(value),
    }));
}

function buildOverviewRows(data, labels) {
  const sections = [
    [labels.sectionProfile, buildProfileRows(data.profile, labels)],
    [labels.sectionSummary, buildSummaryRows(data.summary, labels)],
    [labels.sectionAssignment, buildAssignmentRows(data.currentAssignment, labels)],
    [labels.sectionRanking, buildRankingRows(data.ranking, data.profile, labels)],
  ];
  return sections.flatMap(([section, rows]) => kvSheet(section, rows));
}

export function exportTeacherDetailToExcel(data, labels, filename) {
  const wb = XLSX.utils.book_new();
  const overview = buildOverviewRows(data, labels);
  if (overview.length) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(overview), labels.sheetOverview);
  }

  const attendance = buildAttendanceRows(data.recentAttendance, labels);
  XLSX.utils.book_append_sheet(
    wb,
    attendance.length
      ? XLSX.utils.json_to_sheet(attendance)
      : XLSX.utils.aoa_to_sheet([[labels.noAttendance]]),
    labels.sheetAttendance
  );

  const { monthly, byDay, byEstab } = buildChartRows(data.charts, labels);
  XLSX.utils.book_append_sheet(
    wb,
    monthly.length ? XLSX.utils.json_to_sheet(monthly) : XLSX.utils.aoa_to_sheet([[labels.noData]]),
    labels.sheetMonthly
  );
  XLSX.utils.book_append_sheet(
    wb,
    byDay.length ? XLSX.utils.json_to_sheet(byDay) : XLSX.utils.aoa_to_sheet([[labels.noData]]),
    labels.sheetByDay
  );
  XLSX.utils.book_append_sheet(
    wb,
    byEstab.length ? XLSX.utils.json_to_sheet(byEstab) : XLSX.utils.aoa_to_sheet([[labels.noData]]),
    labels.sheetByEstablishment
  );

  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), filename);
}

export function exportTeacherDetailToCsv(data, labels, filename) {
  const overview = buildOverviewRows(data, labels);
  const attendance = buildAttendanceRows(data.recentAttendance, labels);

  const lines = [];
  lines.push("Section,Field,Value");
  overview.forEach((row) => {
    lines.push(
      [row.Section, row.Field, row.Value]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(",")
    );
  });

  lines.push("");
  if (attendance.length) {
    const headers = Object.keys(attendance[0]);
    lines.push(headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(","));
    attendance.forEach((row) => {
      lines.push(headers.map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(","));
    });
  } else {
    lines.push(`"${labels.noAttendance}"`);
  }

  const blob = new Blob([`\uFEFF${lines.join("\n")}`], { type: "text/csv;charset=utf-8" });
  saveAs(blob, filename);
}

function isSameOriginUrl(src) {
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return true;
  try {
    return new URL(src, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** Strip cross-origin images and Chart.js canvases that break production capture. */
function sanitizeNodeForCapture(root) {
  if (!root) return;

  root.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") || "";
    if (!isSameOriginUrl(src)) {
      img.removeAttribute("src");
      img.style.visibility = "hidden";
    }
  });

  root.querySelectorAll("canvas").forEach((canvas) => {
    const doc = root.ownerDocument;
    const placeholder = doc.createElement("div");
    const chartTitle =
      canvas.closest(".MuiCard-root, .MuiPaper-root")?.querySelector("h6, .MuiTypography-h6")?.textContent?.trim() ||
      "Chart";
    placeholder.textContent = chartTitle;
    placeholder.style.minHeight = "120px";
    placeholder.style.background = "#f8fafc";
    placeholder.style.border = "1px dashed #cbd5e1";
    placeholder.style.borderRadius = "12px";
    placeholder.style.display = "flex";
    placeholder.style.alignItems = "center";
    placeholder.style.justifyContent = "center";
    placeholder.style.color = "#64748b";
    placeholder.style.fontSize = "12px";
    placeholder.style.fontWeight = "600";
    canvas.parentNode?.replaceChild(placeholder, canvas);
  });
}

async function captureSectionCanvas(section) {
  if (!section || section.offsetHeight < 1) return null;

  const scale = Math.min(1.5, window.devicePixelRatio || 1);
  return html2canvas(section, {
    scale,
    useCORS: true,
    allowTaint: false,
    logging: false,
    backgroundColor: "#ffffff",
    scrollX: 0,
    scrollY: -window.scrollY,
    onclone: (_doc, clonedSection) => sanitizeNodeForCapture(clonedSection),
  });
}

function addPdfHeader(doc, meta, margin, pageW) {
  doc.setFontSize(14);
  doc.setTextColor(33, 113, 255);
  doc.text(meta?.title || "Teacher profile", margin, 24);
  if (meta?.subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(meta.subtitle, margin, 38);
  }
}

function addPdfFooter(doc, pageLabel, margin, pageW, pageH, pageIndex, totalPages) {
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`${pageLabel || "Page"} ${pageIndex} / ${totalPages}`, pageW - margin, pageH - 12, {
    align: "right",
  });
}

async function exportTeacherDetailVisualPdf(element, meta, filename) {
  const doc = new jsPDF("p", "pt", "a4");
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  const headerH = meta?.subtitle ? 52 : 40;
  const footerSpace = 24;
  let y = headerH;

  addPdfHeader(doc, meta, margin, pageW);

  const sections = Array.from(element.children).filter((node) => node.offsetHeight > 0);
  if (!sections.length) sections.push(element);

  for (const section of sections) {
    const canvas = await captureSectionCanvas(section);
    if (!canvas) continue;

    const imgData = canvas.toDataURL("image/png");
    const imgProps = doc.getImageProperties(imgData);
    let imgW = contentW;
    let imgH = (imgProps.height * imgW) / imgProps.width;
    const maxH = pageH - headerH - footerSpace;
    if (imgH > maxH) {
      imgH = maxH;
      imgW = (imgProps.width * imgH) / imgProps.height;
    }

    if (y + imgH > pageH - footerSpace) {
      doc.addPage();
      y = margin + 8;
    }

    doc.addImage(imgData, "PNG", margin, y, imgW, imgH);
    y += imgH + 16;
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    addPdfFooter(doc, meta?.pageLabel, margin, pageW, pageH, i, totalPages);
  }

  doc.save(filename);
}

function exportTeacherDetailDataPdf(data, labels, meta, filename) {
  const doc = new jsPDF("p", "pt", "a4");
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  let y = 48;

  addPdfHeader(doc, meta, margin, pageW);

  const addKvTable = (title, rows) => {
    const body = rows.filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "");
    if (!body.length) return;

    if (y > 700) {
      doc.addPage();
      y = margin + 8;
    }

    doc.setFontSize(11);
    doc.setTextColor(55);
    doc.text(title, margin, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [[labels.field || "Field", labels.value || "Value"]],
      body,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [102, 126, 234] },
    });
    y = doc.lastAutoTable.finalY + 14;
  };

  addKvTable(labels.sectionProfile, buildProfileRows(data.profile, labels));
  addKvTable(labels.sectionSummary, buildSummaryRows(data.summary, labels));
  addKvTable(labels.sectionAssignment, buildAssignmentRows(data.currentAssignment, labels));
  addKvTable(labels.sectionRanking, buildRankingRows(data.ranking, data.profile, labels));

  const attendance = buildAttendanceRows(data.recentAttendance, labels);
  if (attendance.length) {
    if (y > 650) {
      doc.addPage();
      y = margin + 8;
    }
    doc.setFontSize(11);
    doc.setTextColor(55);
    doc.text(labels.sheetAttendance, margin, y);
    y += 6;
    const headers = Object.keys(attendance[0]);
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [headers],
      body: attendance.map((row) => headers.map((h) => row[h] ?? "")),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [102, 126, 234] },
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    addPdfFooter(doc, meta?.pageLabel, margin, pageW, pageH, i, totalPages);
  }

  doc.save(filename);
}

export async function exportTeacherDetailToPdf({ element, data, labels, meta, filename }) {
  if (!element) throw new Error("Export element not found");

  try {
    await exportTeacherDetailVisualPdf(element, meta, filename);
    return { mode: "visual" };
  } catch (visualErr) {
    console.warn("[TeacherDetail] Visual PDF failed, using data fallback", visualErr);
    if (!data) throw visualErr;
    exportTeacherDetailDataPdf(data, labels, meta, filename);
    return { mode: "data-fallback" };
  }
}
